/* eslint-disable global-require */
import React, { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { AnalyticsLocationProvider } from 'latitude-analytics';
import { Link } from '@latitude/link';
import { Accordion } from '@latitude/accordion-rebrand';
import {
  BREAKPOINT,
  REBRAND_COLOR,
  PADDING
} from '@latitude/core/utils/constants';
import Layout from '@/components/layout';
import SvgInline from '@latitude/svg-inline';
import { PageContext } from '@/context/PageContext';
import footerData from './data/footer.json';
import PageData from '../../../data/pages/credit-cards/twenty-eight-degree.json';
import ArticleTile from '../../../templates/life-done-better/ArticleTileRebrand';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import {
  CardGroup,
  CardGroupItem
} from '@/components/CardGroupRebrand/CardGroup';
import DegreesCardMicrositeHeader from './_28d-microsite-header-rebrand';
import FeatureCardList from '@/components/FeatureCardListRebrand/FeatureCardListRebrand';
import { CARD_CODE, CARD_DATA } from '@/data/pages/credit-cards/card-offer';
import { AnalyticsContext } from 'latitude-analytics';
import HeroBranded from '@/components/HeroBrandedRebrand/HeroBrandedRebrand';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import Button from '@latitude/button-rebrand';
import FeatureGridV1 from '@/components/FeatureGridV1/FeatureGridV1';
import LabRatesAndFeesColumn from '@/components/LabRatesAndFeesColumnRebrand';
import StickyNavigation from '@/components/StickyNavigationRebrand/StickyNavigation';
import HowToApply from '@/components/HowToApplyRebrand/HowToApplyRebrand';
import PromoBanner from '@/components/PromoBannerRebrand/PromoBannerRebrand';
import PaymentOption from './_payment-options';
import PromoBannerCarousel from '@/components/PromoBannerRebrandCarousel/PromoBannerCarouselRebrand';
import Section from '@latitude/section-rebrand';
import Typography from '@latitude/typography';

const APPLY_URL =
  'https://cards.latitudefinancial.com/28degrees?promo_code=554NBQDA';


const PAGE_NAVIGATION = [
  {
    anchor: 'limited-time-offer-selector',
    label: 'Offers'
  },
  {
    anchor: 'benefits',
    label: 'Benefits'
  },
  {
    anchor: 'rates',
    label: 'Rates & Fees'
  },
  {
    anchor: 'payment-options',
    label: 'Payment options'
  },
  {
    anchor: 'how-to-apply',
    label: 'How to apply'
  }
];

const FEATURE_GRID_DATA = {
  features: [
    {
      icon: <SvgInline name="icon-confetti-rebrand" />,
      headingText: 'First year $0 annual fee',
      paragraphText: (
        <>
          Plus, spend $12k yearly on eligible purchases to have your fee waived
          year after year<sup>6</sup>.
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/credit-cards/28-degrees/spend-tracker"
          >
            Use our spend Tracker
          </Link>
        </>
      )
    },
    {
      icon: <SvgInline name="icon-suitcase-rolling-rebrand" />,
      headingText: 'A card without borders',
      paragraphText: (
        <>
          Whether you're shopping online or abroad, you'll enjoy a $0
          international transaction fee & great exchange rates.
        </>
      )
    },
    {
      icon: <SvgInline name="icon-trophy-rebrand" />,
      headingText: 'Live your best (rewards) life',
      paragraphText: (
        <>
          Earn $10 in Rewards each month when you spend $1,000 on eligible
          purchases<sup>1</sup>. That's up to $120 per year! T&Cs apply.
        </>
      )
    },
    {
      icon: <SvgInline name="icon-trophy-rebrand" />,
      headingText: 'Double your Latitude Rewards',
      paragraphText: (
        <>
          New customers earn an extra $10 in Rewards, when they spend the
          equivalent of AUD $1,000 per month on international purchases
          <sup>1</sup>.
        </>
      )
    }
  ],
  moreFeatureTitle: 'View all benefits',

  moreFeatures: [
    {
      title: `Unmissable every day and travel offers`,
      content: (
        <>
          Enjoy a range of credit back offers, discounts and deals from big
          brands at home and abroad, like{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="https://specials.priceless.com/en-au/cardhome/28°_Global_Platinum_Mastercard?issuerId=201810190038&productId=201904150013"
            trackEventData={{
              label: 'mastercard-priceless-specials',
              location: 'unmissable-everyday-and-travel-offers'
            }}
          >
            Mastercard Priceless Specials,
          </Link>
          Expedia and Wotif.{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/travelpartners/"
            trackEventData={{
              label: 'learn-more',
              location: 'unmissable-everyday-and-travel-offers'
            }}
          >
            Learn More
          </Link>
        </>
      )
    },
    {
      title: `Up to 55 days interest free period#`
    },
    {
      title: `Extensive Rewards catalogue`,
      content: (
        <>
          From grocery and everyday offers and discounts, through to dining,
          cinema entertainment and other unique experiences.
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/credit-cards/28-degrees/latitude-rewards/"
            trackEventData={{
              label: 'learn-more',
              location: 'extensive-rewards-catalogue'
            }}
          >
            Learn More
          </Link>
        </>
      )
    },
    {
      title: `$0 additional card holder fee`,
      content: (
        <>
          Share all the features and benefits without needing to open a second
          account.{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="https://latitudefs.zendesk.com/hc/en-au/articles/36591875170577-Additional-Cardholders"
            trackEventData={{
              label: 'learn-more',
              location: '0-ach-fee'
            }}
          >
            Learn More
          </Link>
        </>
      )
    },
    {
      title: `Complimentary protection for your shopping`,
      content: (
        <>
          Shop with confidence online and overseas with complimentary E-Commerce
          <sup>3</sup> and Purchase Protection insurance<sup>2</sup>.{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/credit-cards/28-degrees/insurances"
            trackEventData={{
              label: 'learn-more',
              location: 'complimentary-protection-for-your-shopping'
            }}
          >
            Learn More
          </Link>
        </>
      )
    },
    {
      title: `Top-rated app to complement your card`,
      content: (
        <>
          With a 4.8/5 App Store rating, the{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/mobile-app/"
            trackEventData={{
              label: 'latitude-app',
              location: 'top-rated-app-to-complemet-your-card'
            }}
          >
            Latitude App
          </Link>{' '}
          helps you stay on top of payments and never miss a due date.
        </>
      )
    },
    {
      title: `Free Flight Delay Pass & Global Data Roaming`,
      content: (
        <>
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/credit-cards/28-degrees/flight-delay-pass/"
            trackEventData={{
              label: 'access-to-a-selection-of-airport-lounges',
              location: 'free-flight-delay-pass-and-global-data-roaming'
            }}
          >
            Access to a selection of airport lounges
          </Link>{' '}
          if your flight is delayed 2+ hours<sup>4</sup>. Stay in touch with 3GB{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/credit-cards/28-degrees/global-data-roaming/"
            trackEventData={{
              label: 'free-global-data-each-year',
              location: 'free-flight-delay-pass-and-global-data-roaming'
            }}
          >
            free global data each year
          </Link>{' '}
          & 15% off any extra data purchased in 150+ countries. T&Cs apply.
        </>
      )
    },
    {
      title: `Around the clock protection`,
      content: (
        <>
          Our specialist team protects your personal and financial information
          from fraud and identity theft.{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/scams-hub"
            trackEventData={{
              label: 'learn-more-about-how-we-protect-you',
              location: 'around-the-clock-protection'
            }}
          >
            Learn more about how we protect you.
          </Link>
        </>
      )
    },
    {
      title: `Great foreign exchange rates`,
      content: `Set by our partners at Mastercard. So you get foreign currencies at the same rate we do.`
    }
  ]
};

const HOW_TO_APPLY_DATA = {
  id: "how-to-apply",
  howToApply: {
    sectionTitle: 'How To apply',
    items: [
      {
        title: 'Apply online',
        description: 'You can check your eligibility and apply in 10 minutes.'
      },
      {
        title: 'Wait a minute',
        description:
          '60 seconds is all it takes for us to respond to your application.'
      },
      {
        title: 'Ready, set, shop',
        description:
          'Simply activate your card when it arrives to start shopping and reaping the rewards.'
      }
    ]
  },
  eligibility: {
    sectionTitle: 'Eligibility',
    items: [
      {
        title: 'You must:',
        list: [
          'Be 18 years or over',
          'A resident of Australia or a temporary resident who legally has the right to work in Australia (and intend to stay) for at least the next 12 months.'
        ]
      },
      {
        title: 'Able to supply',
        list: [
          "A copy of your driver's licence, passport or proof of age card",
          'Details of your income, assets and debts',
          "Employer's name and contact details"
        ]
      }
    ]
  }
};

const KEEP_EXPLORING_INFO = [
  {
    title: 'Find your inspiration',
    thumbnail: 'find-your-inspiration.webp',
    path:
      'https://www.latitudefinancial.com.au/credit-cards/28-degrees/travel-inspiration/'
  },
  {
    title: 'Plan your trip',
    thumbnail: 'europe.jpg',
    path: 'travel-inspiration/#benefits'
  },
  {
    title: 'Calculate your savings',
    thumbnail: 'article_tips_to_help_you_become_a_saver.webp',
    path: 'travel-inspiration/#calculate'
  }
];

// Card data array
const cardData = CARD_DATA.filter(card => card.code !== CARD_CODE['28D']);

const schemaData = {
  '@context': 'https://schema.org',
  contactlessPayment: 'true',
  '@type': 'CreditCard',
  annualPercentageRate: '27.99',
  image:
    'https://assets.latitudefinancial.com/legals/key-fact-sheet/28-degrees/card-28-degrees.png',
  url: 'https://www.latitudefinancial.com.au/credit-cards/28-degrees/',
  category: 'Latitude Travel Credit Cards',
  brand: { '@type': 'Brand', name: 'Latitude Financial Services' },
  amount: { '@type': 'MonetaryAmount', minValue: '1000', currency: 'AUD' },
  name: 'Latitude 28° Global Platinum Mastercard',
  description:
    'Shop with no international transaction or currency conversion fees on purchases online or overseas. Get free global data roaming, ecommerce protection, travel offers and more.',
  requiredCollateral: '$1000 minimum credit limit'
};

const CanstarExclusivePage = props => {
  const gatsbyTealiumEnv = process.env.GATSBY_TEALIUM_ENV || 'unknown';
  const dynamicYieldKey = gatsbyTealiumEnv === 'prod' ? '8790471' : '8790470';

  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};

  console.log({ state });

  /** - END - */

  const [analytics] = useContext(AnalyticsContext);
  useEffect(() => {
    const sourceCode = localStorage.getItem('source_code') || 'IDAAA';
    // adding pageData to analytics
    analytics.pageData = {
      page_pageTitle: '28-degrees',
      page_pageType: 'product-details-page',
      page_siteSection: 'cards',
      page_siteSubsection: '28-degrees',
      product_productid: ['CCAU28D'],
      application_sourcecode: sourceCode || 'IDAAA'
    };
  }, []);

  return (
    <Layout customFooter={footerData} location={props.location} hasSecNav>
      <main>
        <Helmet defer={false}>
          <link
            rel="canonical"
            href="https://www.latitudefinancial.com.au/credit-cards/28-degrees/"
          />
          <title>
            28° Platinum Mastercard Credit Card | Canstar Exclusive
          </title>
          <meta
            name="description"
            content="Travel smart & shop online or overseas with Latitude 28° Mastercard. $0 international transaction or currency conversion fees on purchases and exclusive travel perks"
          />

          <link rel="preconnect" href="//cdn.dynamicyield.com" />
          <link rel="preconnect" href="//st.dynamicyield.com" />
          <link rel="preconnect" href="//rcom.dynamicyield.com" />
          <link rel="dns-prefetch" href="//cdn.dynamicyield.com" />
          <link rel="dns-prefetch" href="//st.dynamicyield.com" />
          <link rel="dns-prefetch" href="//rcom.dynamicyield.com" />
          <script type="text/javascript">
            {`
    window.DY = window.DY || {};
    DY.recommendationContext = { type: "PRODUCT", data: ['28-degrees'] };
  `}
          </script>
          <script
            type="text/javascript"
            src={`//cdn.dynamicyield.com/api/${dynamicYieldKey}/api_dynamic.js`}
          />
          <script
            type="text/javascript"
            src={`//cdn.dynamicyield.com/api/${dynamicYieldKey}/api_static.js`}
          />
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
        </Helmet>
        <div>
          <DegreesCardMicrositeHeader data={state?.secondaryNavData} />
        </div>

        <HeroV2 {...state?.heroBannerData?.[0] || PageData?.contentful?.heroBannerData?.[0]} />

        <StickyNavigation
          items={PAGE_NAVIGATION}
          enableAnchorUpdate={true}
          trackId="sticky-get-started"
          stickyOffset={146}
        />

        <FeatureGridV1
          id="benefits"
          trackingId="lowrate"
          {...FEATURE_GRID_DATA}
        />

        {state?.ratesAndFeesData?.[0] && (
          <LabRatesAndFeesColumn isRate={true} {...state.ratesAndFeesData[0]} />
        )}

        <PaymentOption />
        {state?.promoBannerData?.[0] && <PromoBanner {...state?.promoBannerData?.[0]}/>}

        <HowToApply data={HOW_TO_APPLY_DATA} />
         {state?.promoBannerData?.[1] && <PromoBannerCarousel {...state?.promoBannerData?.[1]}/>}

        <HeroBranded heading={<>Download the Latitude credit card app</>} />

        <FeatureCardList cardList={cardData} />
        <div
          css={`
            background-color: ${REBRAND_COLOR.STONE};
          `}
        >
          <Section
            css={`
              padding-top: ${PADDING.P32};
              padding-bottom: ${PADDING.P32};
              @media (min-width: ${BREAKPOINT.LG}) {
                padding-top: ${PADDING.P64};
                padding-bottom: ${PADDING.P64};
              }
            `}
          >
            <AnalyticsLocationProvider location="Keep exploring">
              <Typography.H4>Keep exploring</Typography.H4>
              <CardGroup>
                {KEEP_EXPLORING_INFO.map((item, index) => (
                  <CardGroupItem key={item.title} id={index}>
                    <ArticleTile target="_self" isBranded {...item} />
                  </CardGroupItem>
                ))}
              </CardGroup>
            </AnalyticsLocationProvider>
          </Section>
        </div>

        <Section
          id="faq"
          css={`
            background-color: ${REBRAND_COLOR.WHITE};
            padding-top: ${PADDING.P32};
            padding-bottom: ${PADDING.P32};
          `}
        >
          <div
            data-contentful={state?.faqData?.[0]?.contentfulID}
            css={`
              display: flex;
              flex-direction: column;
              gap: 32px;
            `}
          >
            <Typography.H4>
              {state?.faqData?.[0]?.title || 'Frequently asked questions'}
            </Typography.H4>
            <AnalyticsLocationProvider
              location={
                state?.faqData?.[0]?.title || 'frequently asked questions'
              }
            >
              <Accordion
                css={`
                  .bg-black-10 {
                    padding: 16px 24px;
                  }
                `}
                items={state?.faqData?.[0]?.data || PageData.faq}
              />
            </AnalyticsLocationProvider>
            <Button
              className={`faq-cta`}
              variant="secondary-blue"
              size="lg"
              ctaText={state?.faqData?.[0]?.cta?.text || 'See all FAQ'}
              ctaUrl={
                state?.faqData?.[0]?.cta?.url ||
                '/credit-cards/28-degrees/faqs/'
              }
              trackEventData={{
                label: state?.faqData?.[0]?.cta?.text || 'See all FAQ',
                location: 'frequently asked questions'
              }}
              data-trackid="see-all-faq"
              css={`
                width: fit-content;
                margin: 0 auto;
              `}
            />
          </div>
        </Section>

        <ImportantInformation
          id="important-Info"
          css={`
            overflow-x: hidden;
            .row {
              @media (min-width: 1600px) {
                padding-left: ${PADDING.P32};
                padding-right: ${PADDING.P32};
              }
            }
          `}
          data={{
            content: {
              importantInformationSectionOne: [
                '# To take advantage of up to 55 days Interest Free on everyday credit card purchases, you need to pay the full closing balance on each statement of account by the applicable due date.',
                "<sup id='note-1'>1</sup> Latitude 28&deg; Global Platinum Mastercard cardholders will be eligible for a $10 Latitude Reward every statement period which is redeemable for an e-gift card from participating retailers when they spend $1,000 or more on eligible transactions using their credit card within the statement period. Any purchases made by an additional cardholder will contribute towards the primary cardholder's spend threshold. Eligible transactions are based on the date posted and do not include cash advances, cash equivalent transactions, card fees or charges, credits, refunds, reimbursements, Shopper's Protection premiums, interest and balance transfers into an account. The account must be open and not in default of the credit contract when the Latitude Rewards is issued. The Latitude Rewards will be issued by EonX Services Pty Ltd and will be available for redemption via the Latitude App or Latitude Service Centre within 30 days from the end of the statement period. The primary cardholder has up to 18 months to redeem from the date of issue. <a href='https://assets.latitudefinancial.com/legals/conditions-of-use/28degrees-au/cou.pdf' target='_blank'>View full terms and conditions.</a>'",
                '<sup id="note-2">2</sup> Purchase Protection Insurance is underwritten and issued by AIG Australia Limited ABN 93 004 727 753, AFSL 381 686, Level 19, 2 Park Street, Sydney NSW 2000 (“AIG”) under a group policy of insurance issued to Mastercard. In arranging the insurance Mastercard acts a group purchasing body under ASIC Corporations (Group Purchasing Bodies) Instrument 2018/751. Eligible persons who can access the complimentary Purchase Protection Insurance are the current holders of the relevant Latitude 28° Global Platinum Mastercard which entitles them to these purchase protection insurance benefits. It is important you read your Latitude 28° Global Platinum Mastercard Purchase Protection Insurance Terms and Conditions. Eligible persons can claim as third-party beneficiaries by virtue of the operation of s48 of the Insurance Contracts Act 1984. Conditions and Exclusions apply to this insurance coverage as set out in the group policy and any insurance document provided to the eligible persons and which both may be amended from time to time. Neither Latitude Finance Australia nor Mastercard guarantees this insurance and do not hold an Australian Financial Services Licence. <a targe="_blank" href="https://assets.latitudefinancial.com/legals/28degreescard/purchase-protection-insurance-termsconds.pdf">View full terms and conditions</a>.',
                '<sup id="note-3">3</sup> E-Commerce Purchase Protection Insurance is underwritten and issued by AIG Australia Limited ABN 93 004 727 753, AFSL 381 686, Level 19, 2 Park Street, Sydney NSW 2000 (“AIG”) under a group policy of insurance issued to Mastercard. In arranging the insurance Mastercard acts a group purchasing body under ASIC Corporations (Group Purchasing Bodies) Instrument 2018/751. Eligible persons who can access the complimentary E-Commerce Purchase Protection Insurance are the current holder of the relevant Latitude 28° Global Platinum Mastercard which entitles them to these e-commerce purchase protection insurance benefits. It is important you read your Latitude 28° Global Platinum Mastercard E-Commerce Purchase Protection Insurance Terms and Conditions. Eligible persons can claim as third-party beneficiaries by virtue of the operation of s48 of the Insurance Contracts Act 1984. Conditions and Exclusions apply to this insurance coverage as set out in the group policy and any insurance document provided to the eligible persons and which both may be amended from time to time. Neither Latitude Finance Australia nor Mastercard guarantees this insurance and do not hold an Australian Financial Services Licence. <a targe="_blank" href="https://assets.latitudefinancial.com/legals/28degreescard/ecommerce-protection-insurance-termscond.pdf">View full terms and conditions</a>.'
              ],
              importantInformationSectionTwo: [
                '<sup id="note-4">4</sup> The Flight Delay Pass gives free access to select airport lounges, alternatively cardholders can use their Flight Delay Pass to offset a dollar amount off the total bill at select dining options. This will vary based on location and affiliated LoungeKey Partners at the time. Please note that not all airports offer Flight Delay Pass and the selected airports may be restricted to certain terminals. Access to and use of the Flight Delay Pass is subject to the Terms of Use which are subject to change. Further details of the list of lounges that cardholders are eligible to visit and the specific conditions applicable to each lounge access (Conditions of Use) may also be accessed in the Terms of Use. Participating lounges and their Conditions of Use are subject to change.',
                'Mastercard and the circles design are registered trademarks of Mastercard International Incorporated.',
                'Apple, the Apple logo, Apple Pay, Apple Watch, Face ID, iPad, iPhone, Safari, and Touch ID are trademarks of Apple Inc., and App Store is a service mark of Apple Inc., registered in the U.S. and other countries.',
                'Samsung and Samsung Pay are trademarks or registered trademarks of Samsung Electronics Co., Ltd.',
                'Garmin, the Garmin logo, and the Garmin delta are trademarks of Garmin Ltd. or its subsidiaries and are registered in one or more countries, including the U.S. Garmin Pay is a trademark of Garmin Ltd. or its subsidiaries.',
                'Google Pay, Android, the Google Pay Logo, Google Play and the Google Play logo are trademarks of Google LLC.',
                '&reg; Registered to BPAY Pty Ltd ABN 69 079 137 518',
                "<sup id='note-5'>5</sup>$300 Latitude Reward is available to new and approved applicants by 28 May 2025 who apply for a Latitude 28° Global Platinum Mastercard and is limited to one offer per applicant. To qualify, you must be approved and fulfil on the terms of the offer by spending $1,000 or more on eligible transactions each statement period for 3 months from the date of your approval. Any purchases made by an additional cardholder will contribute towards the primary cardholder's spend threshold. Eligible transactions are based on the date posted and do not include cash advances, cash equivalent transactions, card fees or charges, credits, refunds, reimbursements, Shopper's Protection premiums, interest and balance transfers into an account. The account must be open and not in default or the credit contract when the redeemable amount is issued. The Latitude Rewards will be issued by EonX Services Pty Ltd and will be available for redemption via the Latitude App or Latitude Service Centre within 30 days of meeting the spend criteria. The primary cardholder has up to 18 months to redeem from the date of issue. Latitude may vary, extend or withdraw this offer at any time without notice.",
                "<sup id='note-6'>6</sup>$0 Monthly Card fees for your first 12 months is available to new and approved applicants by 28 May 2025 who apply for a Latitude 28° Global Platinum Mastercard and is limited to one offer per applicant. After the first year, the monthly card fee reverts to $8 per month (subject to change) and will be debited to your card account on your first statement following your 12-month anniversary and monthly thereafter. Latitude may vary, extend or withdraw this offer at any time without notice.​"
              ]
            }
          }}
          sectionOneColummClasses="col-12 col-lg-6"
          sectionTwoColummClasses="col-12 col-lg-6"
          {...{
            ...state?.importantInfoData?.[0],
            sectionOneColummClasses: 'col-12 col-lg-6',
            sectionTwoColummClasses: 'col-12 col-lg-6'
          }}
        />
      </main>
    </Layout>
  );
};

export default CanstarExclusivePage;
